// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import updateLoadParameter from "@bg/App/Handlers/CustomQuote/UpdateLoadParameters";
import { CustomAppState } from "@bg/App/Store/Reducers";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: CustomAppState) => {
    return {
        query: state.myCustomQuotes.getCustomQuotesParameter.search,
    };
};

const mapDispatchToProps = {
    updateLoadParameter,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface MyListsSearchBoxStyles {
    search?: TextFieldProps;
}

export const searchBoxStyles: MyListsSearchBoxStyles = {
    search: {
        cssOverrides: {
            formField: css`
                flex-direction: row;
                align-items: center;
                label {
                    width: auto;
                    font-weight: ${fontConfig.weight.bold};
                    font-size: ${fontConfig.fontSize.small}px;
                    margin-right: 8px;
                    white-space: nowrap;
                }
                @media (max-width: 650px) {
                    flex-direction: column;
                    align-items: flex-start;
                }
            `,
            formInputWrapper: css`
                margin-top: 0 !important;
            `,
            inputSelect: css`
                padding-left: 32px !important;
                font-size: ${fontConfig.fontSize.small}px;
                height: 32px;
            `,
        },
        iconProps: {
            src: "Search",
            size: 14,
            color: "common.ui1",
            css: css`
                &&&&&& {
                    padding: 0;
                    left: 10px;
                    top: 50%;
                    transform: translateY(-50%);
                    svg {
                        opacity: 0.8;
                        max-height: 14px;
                        max-width: 14px;
                    }
                }
            `,
        },
    },
};

interface MyListsSearchBoxState {
    query?: string;
}

const styles = searchBoxStyles;

class MyCustomQuotesSearchBox extends React.Component<Props, MyListsSearchBoxState> {
    searchTimeoutId: NodeJS.Timeout | undefined;

    constructor(props: Props) {
        super(props);
        this.state = {
            query: props.query,
        };
    }

    handleChange = (event: React.FormEvent<HTMLInputElement>, props: Props) => {
        clearTimeout(this.searchTimeoutId);

        const query = event.currentTarget.value;
        this.setState({ query });

        this.searchTimeoutId = setTimeout(() => {
            props.updateLoadParameter({ search: query });
        }, 250);
    };

    render() {
        return (
            <TextField
                label={translate("Search")}
                data-test-selector="myCustomQuotesByName"
                placeholder={translate("Search quotes by name")}
                {...styles.search}
                value={this.state.query}
                onChange={(event: React.FormEvent<HTMLInputElement>) => this.handleChange(event, this.props)}
            ></TextField>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MyCustomQuotesSearchBox),
    definition: {
        group: "My Custom Quotes" as any,
        displayName: "Search Box",
        allowedContexts: ["MyCustomQuotesPage" as any],
    },
};

export default widgetModule;
